import {
  BaseBenefitPlanPolicyPeriod,
  BenefitPlanTypeEnum,
} from "@platform/openapi-types";
import {
  Column,
  DefaultMenuItem,
  GetMainMenuItems,
  GetMainMenuItemsParams,
  MenuItemDef,
  ProvidedColumnGroup,
} from "ag-grid-community";

import { BenefitPlanGroup, RfpCoverage } from "../../../api/types";
import {
  AddPlanDialogData,
  DeletePlanDialogData,
  DuplicatePlanDialogData,
  NicknamePlanDialogData,
} from "../types/dialog.types";
import {
  isLastCurrentOrNewBusinessGroup,
  isLastPlanGroup,
} from "../../../util/deletePlanCleanup";
import {
  extractColumnContext,
  extractColumnGroupContext,
} from "../utilities/buildPlanGroupColumns";

type MenuActionCallbacks = {
  openAddPlanDialog: (params: AddPlanDialogData) => void;
  openNicknamePlanDialog: (params: NicknamePlanDialogData) => void;
  openDeletePlanDialog: (params: DeletePlanDialogData) => void;
  openDuplicatePlanDialog: (params: DuplicatePlanDialogData) => void;
  getCurrentCoverage?: () => RfpCoverage | undefined;
  benefitPlanGroups?: BenefitPlanGroup[];
  newDeletePlanValidation?: boolean;
  planLevelMenuItems?: GetMainMenuItems;
};

type ColumnInfo = {
  planIdentifier: string;
  planHeaderName: string;
  rawNickname?: string;
  productType?: BenefitPlanTypeEnum;
  planGroupId?: string;
  size?: number;
  position?: number;
};

const SEPARATOR: DefaultMenuItem = "separator";

/**
 * Extracts column information from either a column group or individual column
 */
const extractColumnInfo = (
  columnGroup: ProvidedColumnGroup | null,
  column: Column | null,
): ColumnInfo | null => {
  if (columnGroup) {
    const columnContext = extractColumnGroupContext(columnGroup);
    return {
      planIdentifier: columnGroup.getGroupId(),
      planHeaderName: columnGroup.getColGroupDef()?.headerName || "",
      rawNickname: columnContext?.nickname,
      productType: columnContext?.productType,
      planGroupId: columnContext?.planGroupId,
      size: columnContext?.size,
      position: columnContext?.position,
    };
  }

  if (column) {
    const columnContext = extractColumnContext(column);
    return {
      planIdentifier: column.getColId() || "",
      planHeaderName: column.getColDef()?.headerName || "",
      rawNickname: columnContext?.nickname,
      productType: columnContext?.productType,
      planGroupId: columnContext?.planGroupId,
      size: columnContext?.size,
      position: columnContext?.position,
    };
  }

  return null;
};

const getNotAlternatePolicyPeriod = (
  coverage: RfpCoverage | undefined,
): BaseBenefitPlanPolicyPeriod | undefined =>
  coverage?.benefitPlans.find(
    (plan) => plan.policyPeriod !== BaseBenefitPlanPolicyPeriod.alternate,
  )?.policyPeriod;

// Plan-level actions folded into a single-plan group's top header. Multi-plan
// bands carry no benefitPlanId, so the plan menu resolves empty and this adds
// nothing.
const planLevelSection = (
  params: GetMainMenuItemsParams,
  planLevelMenuItems?: GetMainMenuItems,
): (DefaultMenuItem | MenuItemDef)[] => {
  const items = (planLevelMenuItems?.(params) ?? []).filter(
    (item): item is MenuItemDef => typeof item !== "string",
  );
  return items.length ? [SEPARATOR, ...items] : [];
};

/**
 * Get AG Grid context menu items for column headers
 */
export const getMainMenuItems = (
  params: GetMainMenuItemsParams,
  {
    openAddPlanDialog,
    openNicknamePlanDialog,
    openDeletePlanDialog,
    openDuplicatePlanDialog,
    getCurrentCoverage,
    benefitPlanGroups,
    newDeletePlanValidation,
    planLevelMenuItems,
  }: MenuActionCallbacks,
): (DefaultMenuItem | MenuItemDef)[] => {
  const columnInfo = extractColumnInfo(params.columnGroup, params.column);

  if (!columnInfo?.planIdentifier) {
    return params.defaultItems;
  }

  const {
    planIdentifier,
    planHeaderName,
    rawNickname,
    productType,
    planGroupId,
    size,
    position,
  } = columnInfo;

  // Only show "Nickname plan" on plan-level groups (not document-level groups)
  const nicknamePlanItem: MenuItemDef | undefined = productType
    ? {
        name: "Nickname Plan Group",
        action: () =>
          openNicknamePlanDialog({
            planIdentifier,
            currentNickname: rawNickname ?? "",
            planGroupId,
            productType,
          }),
      }
    : undefined;

  const coverage = getCurrentCoverage?.();

  const newPlanGroup: MenuItemDef | undefined =
    productType && position
      ? {
          name: "New Plan Group",
          subMenu: [
            {
              name: "To right",
              action: () =>
                openAddPlanDialog({
                  productType,
                  insertPosition: "after",
                  position,
                  currentPolicyPeriod: getNotAlternatePolicyPeriod(coverage),
                  coverageId: coverage?.id,
                }),
            },
            {
              name: "To left",
              action: () =>
                openAddPlanDialog({
                  productType,
                  insertPosition: "before",
                  position,
                  currentPolicyPeriod: getNotAlternatePolicyPeriod(coverage),
                  coverageId: coverage?.id,
                }),
            },
          ],
        }
      : undefined;

  const duplicatePlanGroup: MenuItemDef | undefined =
    productType && planGroupId && size
      ? {
          name: "Duplicate Plan Group",
          subMenu: [
            {
              name: "To right",
              action: () =>
                openDuplicatePlanDialog({
                  planGroupId,
                  currentNickname: rawNickname ?? "",
                  productType,
                  size,
                  insertPosition: "after",
                  currentPolicyPeriod: getNotAlternatePolicyPeriod(coverage),
                  coverageId: coverage?.id,
                }),
            },
            {
              name: "To left",
              action: () =>
                openDuplicatePlanDialog({
                  planGroupId,
                  currentNickname: rawNickname ?? "",
                  productType,
                  size,
                  insertPosition: "before",
                  currentPolicyPeriod: getNotAlternatePolicyPeriod(coverage),
                  coverageId: coverage?.id,
                }),
            },
          ],
        }
      : undefined;

  const deleteDisabled = planGroupId
    ? newDeletePlanValidation
      ? isLastPlanGroup(planGroupId, benefitPlanGroups)
      : isLastCurrentOrNewBusinessGroup(planGroupId, benefitPlanGroups)
    : false;

  const deleteDisabledTooltip = newDeletePlanValidation
    ? "Cannot delete the last plan group"
    : "Cannot delete the last current or new business plan group";

  const deletePlanGroup: MenuItemDef | undefined = planGroupId
    ? {
        name: "Delete Plan Group",
        disabled: deleteDisabled,
        tooltip: deleteDisabled ? deleteDisabledTooltip : undefined,
        action: () =>
          openDeletePlanDialog({
            planGroupId,
            planName: planHeaderName,
            willPromote: newDeletePlanValidation
              ? isLastCurrentOrNewBusinessGroup(planGroupId, benefitPlanGroups)
              : false,
          }),
      }
    : undefined;

  return [
    ...(nicknamePlanItem ? [nicknamePlanItem] : []),
    SEPARATOR,
    ...(newPlanGroup ? [newPlanGroup] : []),
    ...(duplicatePlanGroup ? [duplicatePlanGroup] : []),
    ...(deletePlanGroup ? [deletePlanGroup] : []),
    ...planLevelSection(params, planLevelMenuItems),
    SEPARATOR,
    ...params.defaultItems,
  ];
};
